import styles from "./CategorizedNomenclatureListPage.module.scss";
import React from "react";
import { FieldValues, DefaultValues } from "react-hook-form";
import * as yup from "yup";
import { ICategorizedNomenclatureEndpoints } from "./models/categorizedNomenclatureEndpoints";
import {
  ICategorizableItem,
  INomenclatureCategory,
} from "./models/categorizedNomenclatureModels";
import { AppAccordion } from "../components/AppAccordion/AppAccordion";
import { RenderWhen, IfTrue, IfFalse } from "../infrastructure/components/RenderWhen";
import {
  PageTitleSection,
  ItemFormDialog,
  CategoryFormDialog,
  ReorderDialogsSection,
  CategorizedNomenclatureSkeleton,
} from "./components";
import { ICustomCategoryFormConfig } from "./components/CategoryFormDialog";
import { SearchField } from "../components/inputs/searchField/SearchField";
import { useCategorizedNomenclatureListPage } from "./hooks/useCategorizedNomenclatureListPage";

interface CategorizedNomenclatureListPageProps<
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem,
  TCategoryFormModel extends FieldValues = FieldValues
> {
  endpoints: ICategorizedNomenclatureEndpoints;
  title: string;
  description?: string;
  dialogAddTitle: string;
  dialogEditTitle: string;
  emptyListText: string;
  itemTemplate: (
    item: TItemModel | undefined,
    index: number,
    isLoading: boolean,
    actionButtons?: React.ReactNode,
    className?: string,
    onEditItem?: (item: TItemModel) => void
  ) => JSX.Element;
  formSchema: yup.ObjectSchema<any>;
  defaultValues: () => DefaultValues<TFormModel>;
  editOnRowClick?: boolean;
  formFields: React.ReactNode;
  categoryOptions?: { id: number; name: string }[];
  transformPayload?: (data: TFormModel) => Partial<TItemModel>;
  customCategoryConfig?: ICustomCategoryFormConfig<TCategoryFormModel>;
  enableSearch?: boolean;
  uncategorizedEmoji?: string;
}

export const CategorizedNomenclatureListPage = <
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem,
  TCategory extends INomenclatureCategory,
  TCategoryFormModel extends FieldValues = FieldValues
>({
  endpoints,
  title,
  description,
  dialogAddTitle,
  dialogEditTitle,
  emptyListText,
  itemTemplate,
  formSchema,
  defaultValues,
  formFields,
  editOnRowClick = false,
  transformPayload,
  customCategoryConfig,
  enableSearch = false,
  uncategorizedEmoji,
}: CategorizedNomenclatureListPageProps<TFormModel, TItemModel, TCategoryFormModel>) => {
  const {
    items,
    categories,
    isLoading,
    isSubmitting,
    accordionTabs,
    activeAccordionIndexes,
    setActiveAccordionIndexes,
    isSearching,
    onSearch,
    itemForm,
    itemDialogOpen,
    editingItemId,
    itemDialogTitle,
    onHideItemDialog,
    onSubmitItem,
    categoryDialogOpen,
    editingCategoryId,
    onHideCategoryDialog,
    onSubmitCategory,
    showReorderButton,
    reorderItemsDialogOpen,
    reorderCategoriesDialogOpen,
    setReorderItemsDialogOpen,
    setReorderCategoriesDialogOpen,
    itemsForReorder,
    categoriesForReorder,
    handleReorderItems,
    handleReorderCategories,
  } = useCategorizedNomenclatureListPage<TFormModel, TItemModel, TCategory>({
    endpoints,
    dialogAddTitle,
    dialogEditTitle,
    emptyListText,
    itemTemplate,
    formSchema,
    defaultValues,
    editOnRowClick,
    transformPayload,
    enableSearch,
    uncategorizedEmoji,
  });

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <PageTitleSection
          title={title}
          description={description}
          isLoading={isLoading}
          showReorderButton={showReorderButton}
          onReorderClick={() => setReorderCategoriesDialogOpen(true)}
        />

        {enableSearch && (
          <SearchField
            onChange={onSearch}
            className={styles.search_field}
          />
        )}

        <div className={styles.scrollable_content}>
          <RenderWhen condition={isLoading}>
            <IfTrue>
              <CategorizedNomenclatureSkeleton />
            </IfTrue>
            <IfFalse>
              {isSearching && accordionTabs.length === 0 ? (
                <div className={styles.no_results}>
                  <i className="pi pi-search" />
                  <p>Няма намерени резултати</p>
                </div>
              ) : (
                <AppAccordion
                  tabs={accordionTabs}
                  multiple
                  activeIndex={activeAccordionIndexes}
                  onTabChange={(e) => setActiveAccordionIndexes(e.index as number[])}
                  className={styles.accordion}
                />
              )}
            </IfFalse>
          </RenderWhen>
        </div>
      </div>

      <ItemFormDialog
        visible={itemDialogOpen}
        title={itemDialogTitle}
        form={itemForm}
        formFields={formFields}
        items={items}
        editingItemId={editingItemId}
        isSubmitting={isSubmitting}
        onHide={onHideItemDialog}
        onSubmit={onSubmitItem}
      />

      <CategoryFormDialog<TCategory, TCategoryFormModel>
        visible={categoryDialogOpen}
        editingCategoryId={editingCategoryId}
        categories={categories}
        isSubmitting={isSubmitting}
        onHide={onHideCategoryDialog}
        onSubmit={onSubmitCategory as unknown as (data: TCategoryFormModel) => void}
        customConfig={customCategoryConfig}
      />

      <ReorderDialogsSection
        itemsDialogVisible={reorderItemsDialogOpen}
        itemsForReorder={itemsForReorder}
        onHideItemsDialog={() => setReorderItemsDialogOpen(false)}
        onSaveItemsReorder={handleReorderItems}
        categoriesDialogVisible={reorderCategoriesDialogOpen}
        categoriesForReorder={categoriesForReorder}
        onHideCategoriesDialog={() => setReorderCategoriesDialogOpen(false)}
        onSaveCategoriesReorder={handleReorderCategories}
        loading={isSubmitting}
      />
    </div>
  );
};

export default CategorizedNomenclatureListPage;
